import { Photo } from './Photo';

/**
 * The NxtHire mark: an N built from a factory, with a growth arrow through it.
 *
 * Only the mark. The artwork is a stacked lockup — mark, "NxtHire", "WORKFORCE
 * SOLUTIONS", tagline — and the header and footer already set those words as
 * live text from `SUPPLIER`. Text stays sharp at any size, translates into the
 * eleven languages, and can be read aloud; a picture of the same words does
 * none of that, and at the 36px this renders at the tagline would be a smudge.
 *
 * `onDark` is not a style preference. The mark is navy #083360, the footer
 * ground is #253029, and navy on that measures 1.08:1 — not low contrast,
 * invisible. Without a second variant the footer would show an orange arrow
 * floating over nothing. The dark variant repaints the navy in ink-100 and
 * keeps the arrow. The factory and the figures are knocked out of the N rather
 * than painted on it, so they are holes in both files and the page colour shows
 * through: light-on-dark in the header, dark-on-light in the footer, from the
 * same drawing.
 *
 * Raster, where the mark it replaces was hand-authored SVG. That SVG existed
 * because the source PNG was 355 KB and a header glyph cannot cost that. These
 * are 4 KB and 7 KB, picked by DPR — the objection was to the weight, not to
 * the format, and redrawing a factory, three figures, a staircase and a curved
 * arrow by hand would produce something that is not quite the company's logo.
 *
 * Built by `scripts/build-logo.py`, which is where the crop and the colours are.
 */
export function BrandMark({
  className = 'h-8 w-8',
  onDark = false,
}: {
  className?: string;
  /** True on the footer and any other dark ground. */
  onDark?: boolean;
}) {
  return (
    <Photo
      name={onDark ? 'logo-mark-light' : 'logo-mark'}
      widths={[72, 108]}
      width={108}
      height={108}
      sizes="36px"
      /*
       * Named rather than decorative, unlike the hero photograph. The wordmark
       * beside it is hidden below `sm`, so on a handset this image is the only
       * content of the link home — and a link whose only content is an image
       * with an empty alt has no accessible name at all.
       */
      alt="NxtHire"
      priority
      className={className}
    />
  );
}
